<template>
  <ListingTemplate customClass="product-quotation-history">
    <template v-slot:body>
      <ListingHeader>
        <template v-slot:content>
          <v-flex class="pt-0">
            <div>
              <h1 class="form-title mb-1">Quotation history</h1>
              <p class="text--secondary font-size-12 mb-0">
                Every quotation this product has appeared on. Read-only.
              </p>
            </div>
          </v-flex>
          <v-flex class="pt-0 d-flex justify-flex-end align-center">
            <v-select
              v-model="status"
              :items="statusList"
              :disabled="dataLoading"
              :loading="dataLoading"
              :menu-props="{ offsetY: true }"
              hide-details
              dense
              flat
              filled
              solo
              style="width: 130px; margin-right: 6px; font-size: 10px"
              item-color="cyan"
              color="cyan"
              class="pt-0 mt-0 mr-3 listing-status main-listing-status-filter quotation-status-filter"
              item-text="text"
              item-value="value"
              @change="applyFilters"
            >
              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-title
                    v-html="'No Status(s) Found.'"
                  ></v-list-item-title>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <span class="font-size-12">{{ item.text }}</span>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-action class="mr-0">
                  <v-chip
                    style="height: 15px; width: 12px; padding: 0"
                    :color="item.color"
                  >
                  </v-chip>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="font-size-12">{{
                    item.text
                  }}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-select>
            <v-btn
              v-if="false"
              depressed
              class="mx-2 custom-bold-button"
              color="cyan white--text"
              :disabled="dataLoading"
              @click="exportProductQuotation"
            >
              <v-icon dark left>mdi-database-export</v-icon> Export
            </v-btn>
          </v-flex>
        </template>
      </ListingHeader>

      <v-row class="mt-1 mb-3">
        <v-col cols="12" sm="4" md="3">
          <div class="grey lighten-5 pa-4 d-flex flex-column justify-center">
            <span class="text--secondary font-size-12 font-weight-600"
              >Times quoted</span
            >
            <strong class="color-custom-blue font-size-20">{{
              totalRows
            }}</strong>
          </div>
        </v-col>
        <v-col v-if="!isSalesPerson" cols="12" sm="4" md="3">
          <div class="grey lighten-5 pa-4 d-flex flex-column justify-center">
            <span class="text--secondary font-size-12 font-weight-600"
              >Average quoted</span
            >
            <strong class="color-custom-blue font-size-20">{{
              formatMoney(averageQuoted)
            }}</strong>
          </div>
        </v-col>
        <v-col cols="12" sm="4" md="3">
          <div class="grey lighten-5 pa-4 d-flex flex-column justify-center">
            <span class="text--secondary font-size-12 font-weight-600"
              >Range</span
            >
            <strong class="color-custom-blue font-size-20">
              {{ formatMoney(minQuoted) }} – {{ formatMoney(maxQuoted) }}
            </strong>
          </div>
        </v-col>
      </v-row>

      <ListingTable
        :columnCount="5"
        :dataLoading="dataLoading"
        :rowData="historyRows"
      >
        <template v-slot:thead>
          <thead>
            <tr>
              <th class="simple-table-th">Customer</th>
              <th class="simple-table-th">Quotation no.</th>
              <th class="simple-table-th">Status</th>
              <th class="simple-table-th text-right">Quoted at</th>
              <th class="simple-table-th">Quotation date</th>
            </tr>
          </thead>
        </template>
        <template v-slot:tbody>
          <tbody class="custom-border-bottom">
            <tr
              v-for="row in historyRows"
              :key="row.line_item_id"
              class="alternate-listing-row"
              @click="openQuotation(row.quotation_id)"
            >
              <td class="simple-table-td">
                <div v-if="row.customer_company_name" class="font-weight-600">
                  {{ row.customer_company_name }}
                </div>
                <div
                  v-if="row.customer_name"
                  class="text--secondary font-size-12"
                >
                  {{ row.customer_name }}
                </div>
                <div
                  v-if="!row.customer_company_name && !row.customer_name"
                  class="font-weight-600"
                >
                  —
                </div>
              </td>
              <td class="simple-table-td">
                <v-chip
                  small
                  label
                  link
                  color="cyan"
                  outlined
                  text-color="cyan"
                  class="font-weight-600"
                  :to="
                    getDefaultRoute('quotation.detail', {
                      params: { id: row.quotation_id },
                    })
                  "
                  @click.stop
                >
                  {{ row.quotation_barcode || "—" }}
                </v-chip>
              </td>
              <td class="simple-table-td">
                <v-chip
                  small
                  label
                  :color="row.quotation_status_color || 'grey'"
                  :text-color="row.quotation_status_textcolor || 'white'"
                  class="font-weight-600"
                >
                  {{ quotationStatus(row) }}
                </v-chip>
              </td>
              <td class="simple-table-td text-right font-weight-600">
                {{ formatMoney(quotedAmount(row)) }}
              </td>
              <td class="simple-table-td">
                {{ formatDate(row.date || "—") }}
              </td>
            </tr>
            <tr v-if="!historyRows.length && !dataLoading">
              <td colspan="5" class="py-4">
                <p
                  class="m-0 row-not-found text-center font-weight-500 font-size-16"
                >
                  <img
                    :src="$assetURL('media/error/empty.png')"
                    class="row-not-found-image"
                  />
                  Uhh... There are no quotations for this product at the moment.
                </p>
              </td>
            </tr>
          </tbody>
        </template>
      </ListingTable>

      <ListingFooter
        :dataLoading="dataLoading"
        :showingFrom="showingFrom"
        :showingTo="showingTo"
        :totalRows="totalRows"
        :currentPage="currentPage"
        :totalPages="totalPages"
      ></ListingFooter>
      <!-- <p class="quotation-history-note mb-0">
        Records are created in the quotation module and cannot be edited here.
      </p> -->
    </template>
  </ListingTemplate>
</template>

<script>
import { QUERY } from "@/core/services/store/request.module";
import JwtService from "@/core/services/jwt.service";
import CommonMixin from "@/core/plugins/common-mixin";
import ListingTemplate from "@/view/pages/partials/Listing-Template.vue";
import ListingTable from "@/view/pages/partials/Listing-Table.vue";
import ListingHeader from "@/view/pages/partials/Listing-Header.vue";
import ListingFooter from "@/view/pages/partials/Listing-Footer.vue";
import { PaginationEventBus } from "@/core/lib/pagination.lib";

export default {
  name: "products-quotation-history",
  components: { ListingTemplate, ListingTable, ListingHeader, ListingFooter },
  mixins: [CommonMixin],
  props: {
    detail: { type: Object, required: true, default: () => ({}) },
    type: { type: String, default: "product" },
  },
  data() {
    return {
      dataLoading: false,
      historyRows: [],
      quotationSummary: {
        count: 0,
        average_rate: 0,
        min_rate: 0,
        max_rate: 0,
      },
      statusList: [
        {
          text: "All Statuses",
          value: "all",
          color: "cyan",
        },
        {
          text: "Draft",
          value: "draft",
          color: "cyan",
        },
        {
          text: "Accepted",
          value: "accepted",
          color: "green",
        },
        {
          text: "Rejected",
          value: "rejected",
          color: "red",
        },
        {
          text: "Sent",
          value: "sent",
          color: "orange",
        },
        {
          text: "Cancelled",
          value: "cancelled",
          color: "red",
        },
      ],
      status: "all",
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
    };
  },
  computed: {
    isSalesPerson() {
      return Number(JwtService.currentUser()?.role) === 5;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalRows / this.perPage));
    },
    showingFrom() {
      return this.totalRows ? (this.currentPage - 1) * this.perPage + 1 : 0;
    },
    showingTo() {
      return Math.min(this.currentPage * this.perPage, this.totalRows);
    },
    averageQuoted() {
      return Number(this.quotationSummary.average_rate || 0);
    },
    minQuoted() {
      return Number(this.quotationSummary.min_rate || 0);
    },
    maxQuoted() {
      return Number(this.quotationSummary.max_rate || 0);
    },
  },
  watch: {
    "detail.id": {
      immediate: true,
      handler() {
        this.currentPage = 1;
        this.getQuotationHistory();
      },
    },
  },
  methods: {
    getProductTypeValue() {
      if (this.detail && this.detail.product_type) {
        return this.detail.product_type;
      }

      if (this.type === "service") {
        return "service";
      }

      return "goods";
    },
    getQuotationHistory() {
      if (!this.detail || !Number(this.detail.id)) return;
      this.dataLoading = true;
      this.$store
        .dispatch(QUERY, {
          url: "product/quotations",
          data: {
            per_page: this.perPage,
            current_page: this.currentPage,
            status: this.status || "all",
            product_id: Number(this.detail.id),
            product_type: this.getProductTypeValue(),
          },
        })
        .then((pageResponse) => {
          this.historyRows = pageResponse.data.rows || [];
          this.totalRows = Number(pageResponse.data.total_rows || 0);
          this.quotationSummary = pageResponse.data.summary || {
            count: 0,
            average_rate: 0,
            min_rate: 0,
            max_rate: 0,
          };
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.dataLoading = false;
        });
    },

    applyFilters() {
      this.currentPage = 1;
      this.getQuotationHistory();
    },
    updatePagination(page) {
      if (page === this.currentPage) return;
      this.currentPage = page;
      this.getQuotationHistory();
    },
    quotedAmount(row) {
      return Number(row.rate || 0);
    },
    quotationStatus(row) {
      return (
        row.quotation_status_text ||
        {
          1: "Draft",
          2: "Accepted",
          3: "Rejected",
          4: "Pending",
          5: "Sent",
          9: "Expired",
        }[row.quotation_status] ||
        "—"
      );
    },

    openQuotation(id) {
      this.$router.push(
        this.getDefaultRoute("quotation.detail", { params: { id } })
      );
    },
  },
  mounted() {
    PaginationEventBus.$on("update:pagination", this.updatePagination);
  },
  beforeDestroy() {
    PaginationEventBus.$off("update:pagination", this.updatePagination);
  },
};
</script>

<style scoped>
.product-quotation-history ::v-deep .custom-listing-table thead {
  display: table-header-group !important;
}

.quotation-history-note,
.quotation-history-note {
  text-align: right;
  margin-top: -32px;
  padding-right: 12px;
}
@media (max-width: 959px) {
  .quotation-history-note {
    margin-top: 0;
    text-align: left;
  }
}
</style>
